import React, { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createBreastGeometry, mirrorGeometry } from '../utils/bustGeometry';
import { createSkinMaterial } from '../utils/skinShader';
import { exportSTL, exportOBJ } from '../utils/exporters';

const BustViewer = ({ params, isPanelOpen, onReady, onAutoRotateOff }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rebuildRef = useRef(null);
  const autoRotateRef = useRef(true);
  const setSizeRef = useRef(null);
  const panelStateRef = useRef(isPanelOpen);

  useEffect(() => {
    panelStateRef.current = isPanelOpen;
    if (setSizeRef.current) setSizeRef.current();
  }, [isPanelOpen]);

  // Rebuild when params change
  useEffect(() => {
    if (rebuildRef.current) rebuildRef.current(params);
  }, [params]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ── Renderer ──────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.toneMappingExposure = 1.0;
    mount.appendChild(renderer.domElement);

    const setSize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      
      const panelOpen = panelStateRef.current;
      // On desktop, shift rightwards (moves model left)
      const offsetX = (w > 768 && panelOpen) ? 184 : 0;
      // On mobile, shift downwards (moves model UP) to be in upper half
      const offsetY = (w <= 768 && panelOpen) ? h * 0.25 : 0;
      
      camera.setViewOffset(w, h, offsetX, offsetY, w, h);
      
      // Adjust camera zoom for narrow screens so the model width fits the screen.
      // PerspectiveCamera fits vertically by default causing horizontal cropping on portrait screens.
      const targetAspect = 0.9;
      const mobileZoomOut = w <= 639 ? 0.72 : 1.0;
      if (camera.aspect < targetAspect) {
        camera.zoom = (camera.aspect / targetAspect) * mobileZoomOut;
      } else {
        camera.zoom = 1.0 * mobileZoomOut;
      }
      
      camera.updateProjectionMatrix();
    };
    setSizeRef.current = setSize;

    // ── Scene ─────────────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x030405);
    scene.fog = new THREE.FogExp2(0x030405, 0.025);

    // ── Camera ────────────────────────────────────────────────────────────
    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 60);
    camera.position.set(0, 0.2, 5.0);
    setSize();

    // ── Controls ──────────────────────────────────────────────────────────
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance = 1.5;
    controls.maxDistance = 14;
    controls.target.set(0, 0, 0);
    controls.maxPolarAngle = Math.PI * 0.9;

    controls.addEventListener('start', () => {
      if (autoRotateRef.current) {
        autoRotateRef.current = false;
        if (onAutoRotateOff) onAutoRotateOff();
      }
    });

    // ── Grid & floor ──────────────────────────────────────────────────────
    /* Grid removed per request */
    /* Floor removed to prevent intersection with large geometry sizes */

    // ── Shadow light ──────────────────────────────────────────────────────
    const shadowLight = new THREE.DirectionalLight(0x000000, 0);
    shadowLight.position.set(-2.5, 4, 3.5);
    shadowLight.castShadow = true;
    shadowLight.shadow.mapSize.set(1024, 1024);
    shadowLight.shadow.camera.near = 0.5;
    shadowLight.shadow.camera.far = 20;
    shadowLight.shadow.camera.left = -4;
    shadowLight.shadow.camera.right = 4;
    shadowLight.shadow.camera.top = 4;
    shadowLight.shadow.camera.bottom = -4;
    scene.add(shadowLight);

    // ── Breast group ──────────────────────────────────────────────────────
    const breastGroup = new THREE.Group();
    scene.add(breastGroup);
    sceneRef.current = { scene, breastGroup };

    let leftMesh = null, rightMesh = null;
    let mats = [];

    let prevVolume = params.volume;

    function rebuild(p) {
      // Dispose old
      if (leftMesh) { breastGroup.remove(leftMesh); leftMesh.geometry.dispose(); }
      if (rightMesh) { breastGroup.remove(rightMesh); rightMesh.geometry.dispose(); }
      mats.forEach(m => m.dispose());
      mats = [];

      const geom = createBreastGeometry(p);
      const mirGeom = mirrorGeometry(geom);
      const matL = createSkinMaterial(p);
      const matR = createSkinMaterial(p);
      mats = [matL, matR];

      leftMesh = new THREE.Mesh(geom, matL);
      // Automatically calculate spacing based on volume so breasts barely intersect
      const baseExtent = 1.18; // Approx maximum X-extent of the geometry
      const cleavageOverlap = 0.08; // Slight overlap for natural cleavage connection
      const dynamicSpacing = p.volume * (baseExtent - cleavageOverlap);
      leftMesh.position.x = -dynamicSpacing;
      leftMesh.castShadow = true;

      rightMesh = new THREE.Mesh(mirGeom, matR);
      rightMesh.position.x = dynamicSpacing;
      rightMesh.castShadow = true;

      breastGroup.add(leftMesh, rightMesh);

      // Adjust camera zoom to fit model within view based on volume scaling
      if (prevVolume && p.volume !== prevVolume && controls) {
        const ratio = p.volume / prevVolume;
        const curDist = camera.position.distanceTo(controls.target);
        
        let targetDist = curDist * ratio;
        targetDist = Math.max(controls.minDistance, Math.min(controls.maxDistance, targetDist));

        const dir = camera.position.clone().sub(controls.target).normalize();
        camera.position.copy(controls.target).add(dir.multiplyScalar(targetDist));
        // Sync OrbitControls internal state so it doesn't revert the new position
        controls.saveState();
        controls.update();
        // Re-apply camera zoom (setSize reads panelStateRef which is always current)
        setSize();
      }
      prevVolume = p.volume;
    }

    rebuild(params);
    rebuildRef.current = rebuild;

    // ── Animation ─────────────────────────────────────────────────────────
    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);

      if (autoRotateRef.current) {
        controls.autoRotate = false; // Disable native endless spin
        const time = performance.now() * 0.0012; // Speed of circular pan
        
        // Target an arc purely from the front side.
        // Clockwise circle:
        // Azimuth (left/right) -> sine
        // Polar (up/down) -> cosine (inverted for clockwise)
        const targetAzi = Math.sin(time) * 0.45; // Swing width
        const targetPol = Math.PI / 2 - Math.cos(time) * 0.3; // Swing height

        const offset = camera.position.clone().sub(controls.target);
        const curSpherical = new THREE.Spherical().setFromVector3(offset);
        
        // Smoothly interpolate towards the target circle path
        // This ensures dragging doesn't abruptly snap, and if dragged away,
        // it beautifully glides back to the front observation circle.
        curSpherical.theta += (targetAzi - curSpherical.theta) * 0.05;
        curSpherical.phi += (targetPol - curSpherical.phi) * 0.05;
        
        offset.setFromSpherical(curSpherical);
        camera.position.copy(controls.target).add(offset);
      } else {
        controls.autoRotate = false; // Ensure it stays off if toggled
      }

      controls.update();

      const cp = camera.position;
      mats.forEach(m => {
        if (m.uniforms?.uCameraPos) m.uniforms.uCameraPos.value.copy(cp);
      });

      renderer.render(scene, camera);
    };
    animate();

    // ── Resize ────────────────────────────────────────────────────────────
    const ro = new ResizeObserver(setSize);
    ro.observe(mount);

    // ── Expose exports ────────────────────────────────────────────────────
    if (onReady) {
      onReady({
        exportSTL: () => exportSTL(breastGroup),
        exportOBJ: () => exportOBJ(breastGroup),
        setAutoRotate: (v) => { autoRotateRef.current = v; },
      });
    }

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
      mats.forEach(m => m.dispose());
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={mountRef} data-testid="bust-viewer" className="absolute inset-0" style={{ cursor: 'grab' }}>
      {/* Scan-line overlay */}
      <div className="absolute inset-0 pointer-events-none z-10"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.04) 3px, rgba(0,0,0,0.04) 4px)' }} />
      {/* Corner brackets */}
      <div className="absolute top-20 left-4 w-8 h-8 border-t border-l border-[#00F0FF]/30 pointer-events-none z-20" />
      <div className={`absolute top-20 w-8 h-8 border-t border-r border-[#00F0FF]/30 pointer-events-none z-20 transition-all duration-300 ${isPanelOpen ? 'right-4 md:right-[368px]' : 'right-4'}`} />
      <div className={`absolute left-4 w-8 h-8 border-b border-l border-[#00F0FF]/30 pointer-events-none z-20 transition-all duration-300 ${isPanelOpen ? 'bottom-[calc(50vh+16px)] md:bottom-4' : 'bottom-4'}`} />
      <div className={`absolute w-8 h-8 border-b border-r border-[#00F0FF]/30 pointer-events-none z-20 transition-all duration-300 ${isPanelOpen ? 'right-4 md:right-[368px] bottom-[calc(50vh+16px)] md:bottom-4' : 'right-4 bottom-4'}`} />
      {/* HUD label */}
      <div className={`absolute left-6 pointer-events-none z-20 flex flex-col gap-1 transition-all duration-300 ${isPanelOpen ? 'bottom-[calc(50vh+24px)] md:bottom-6' : 'bottom-6'}`}>
        <span className="text-[10px] font-mono tracking-[0.2em] text-[#00F0FF]/25 uppercase">Drag to rotate · Scroll to zoom</span>
      </div>
    </div>
  );
};

export default BustViewer;
